import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Check, Copy, Download, Eye, Heart, Languages, Search, Sparkles, Trash2, X } from 'lucide-react';
import { allThemes } from '../data/styles';
import { getThemeCategory, getThemeSearchText, THEME_CATEGORIES, type ThemeCategory } from '../data/themeCategories';
import { PreviewFrame } from './PreviewFrame';
import { ViewportSwitcher, type Viewport } from './ViewportSwitcher';
import { AuthButton } from './AuthButton';
import { BrandLogo } from './BrandLogo';
import { ThemeMiniPreview } from './ThemeMiniPreview';
import { ValueWorkbench } from './ValueWorkbench';
import { appShellColors } from '../styles/appShell';
import { exportTheme, generateDesignMd, generateToolPrompt } from '../utils/designMdExporter';
import { getAccessibleAccentColor, getAccessibleTextColor } from '../utils/color';
import { copyTextToClipboard } from '../utils/clipboard';
import { useAuth } from '../contexts/AuthContext';
import {
  clearShortlistRecords,
  ensureProfile,
  fetchCustomThemes,
  fetchExportHistory,
  fetchShortlistRecords,
  removeShortlistRecord,
  saveExportHistory,
  syncShortlistRecord,
  type CustomThemeRecord,
  type ExportHistoryRecord,
} from '../utils/designLabData';
import {
  appCopy,
  getCategoryLabel,
  getDensityLabel,
  getInitialLocale,
  getThemeCardSummary,
  persistLocale,
  type Locale,
} from '../i18n';
import type { Theme } from '../types/theme';

type PageType = 'landing' | 'dashboard';
type CategoryFilter = 'All' | ThemeCategory;
type CopyStatus = 'idle' | 'copied' | 'failed';

const SHORTLIST_STORAGE_KEY = 'designlab-ui-shortlist';

const shellViewportTheme = {
  primary: appShellColors.primary,
  background: appShellColors.background,
  text: appShellColors.textPrimary,
  surface: appShellColors.surface,
  border: appShellColors.border,
};

interface PageTypeToggleProps {
  value: PageType;
  locale: Locale;
  onChange: (pageType: PageType) => void;
}

function getInitialShortlist(): string[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const rawValue = window.localStorage.getItem(SHORTLIST_STORAGE_KEY);
    const parsedValue = rawValue ? JSON.parse(rawValue) : [];
    if (!Array.isArray(parsedValue)) {
      return [];
    }

    const knownThemeIds = new Set(allThemes.map((theme) => theme.id));
    return parsedValue.filter((themeId): themeId is string => (
      typeof themeId === 'string' && knownThemeIds.has(themeId)
    ));
  } catch {
    return [];
  }
}

function persistShortlist(themeIds: string[]) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(SHORTLIST_STORAGE_KEY, JSON.stringify(themeIds));
  }
}

function PageTypeToggle({ value, locale, onChange }: PageTypeToggleProps) {
  const copy = appCopy[locale];
  const options: Array<{ value: PageType; label: string }> = [
    { value: 'landing', label: copy.pageTypes.landing },
    { value: 'dashboard', label: copy.pageTypes.dashboard },
  ];

  return (
    <div
      className="grid min-h-11 w-[208px] flex-shrink-0 grid-cols-2 items-center gap-1 rounded-lg border p-1 sm:w-[228px]"
      style={{ backgroundColor: appShellColors.surface, borderColor: appShellColors.border }}
    >
      {options.map((option) => {
        const isActive = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={isActive}
            className="min-h-11 truncate rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              backgroundColor: isActive ? appShellColors.primary : 'transparent',
              color: isActive ? appShellColors.onPrimary : appShellColors.textSecondary,
              ['--tw-ring-color' as string]: appShellColors.focusRing,
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function StyleGallery() {
  const { user, status: authStatus } = useAuth();
  const [selectedTheme, setSelectedTheme] = useState<Theme | null>(null);
  const [pageType, setPageType] = useState<PageType>('landing');
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('All');
  const [locale, setLocale] = useState<Locale>(() => getInitialLocale());
  const [shortlistedThemeIds, setShortlistedThemeIds] = useState<string[]>(() => getInitialShortlist());
  const [shortlistOpen, setShortlistOpen] = useState(false);
  const [valueWorkbenchOpen, setValueWorkbenchOpen] = useState(false);
  const [customThemes, setCustomThemes] = useState<CustomThemeRecord[]>([]);
  const [exportHistory, setExportHistory] = useState<ExportHistoryRecord[]>([]);
  const [libraryStatus, setLibraryStatus] = useState<'idle' | 'syncing' | 'error'>('idle');
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');
  const copy = appCopy[locale];
  const themeLibrary = useMemo(() => [
    ...customThemes.map((record) => record.theme),
    ...allThemes,
  ], [customThemes]);

  useEffect(() => {
    persistLocale(locale);
    document.documentElement.lang = locale === 'zh' ? 'zh-CN' : 'en';
    document.title = copy.title;
  }, [copy.title, locale]);

  useEffect(() => {
    persistShortlist(shortlistedThemeIds);
  }, [shortlistedThemeIds]);

  const refreshUserLibrary = async () => {
    if (!user) {
      setCustomThemes([]);
      setExportHistory([]);
      return;
    }

    setLibraryStatus('syncing');
    try {
      await ensureProfile(user);
      const [shortlistRecords, customThemeRecords, exportRecords] = await Promise.all([
        fetchShortlistRecords(user.id),
        fetchCustomThemes(user.id),
        fetchExportHistory(user.id),
      ]);

      setCustomThemes(customThemeRecords);
      setExportHistory(exportRecords);

      setShortlistedThemeIds((currentThemeIds) => {
        const freshKnownThemeIds = new Set([
          ...allThemes.map((theme) => theme.id),
          ...customThemeRecords.map((record) => record.theme.id),
        ]);
        const cloudThemeIds = shortlistRecords
          .map((record) => record.theme_id)
          .filter((themeId) => freshKnownThemeIds.has(themeId));
        const mergedThemeIds = [...new Set([...currentThemeIds, ...cloudThemeIds])];

        Promise.all(
          mergedThemeIds.map((themeId) => syncShortlistRecord(user.id, themeId, pageType, viewport)),
        ).catch(() => setLibraryStatus('error'));

        return mergedThemeIds;
      });

      setLibraryStatus('idle');
    } catch {
      setLibraryStatus('error');
    }
  };

  useEffect(() => {
    if (authStatus !== 'signed-in' || !user) {
      setCustomThemes([]);
      setExportHistory([]);
      return;
    }

    refreshUserLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, user?.id]);

  useEffect(() => {
    if (copyStatus === 'idle') {
      return;
    }

    const timeoutId = window.setTimeout(() => setCopyStatus('idle'), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [copyStatus]);

  const filteredThemes = themeLibrary.filter((theme) => {
    const matchesCategory = selectedCategory === 'All' || getThemeCategory(theme) === selectedCategory;
    const query = searchQuery.trim().toLowerCase();
    const matchesQuery = query.length === 0 || getThemeSearchText(theme).includes(query);
    return matchesCategory && matchesQuery;
  });

  const categoryCounts = THEME_CATEGORIES.reduce((acc, category) => {
    acc[category] = themeLibrary.filter((theme) => getThemeCategory(theme) === category).length;
    return acc;
  }, {} as Record<ThemeCategory, number>);

  const shortlistedThemes = useMemo(() => {
    const themeById = new Map(themeLibrary.map((theme) => [theme.id, theme]));
    return shortlistedThemeIds
      .map((themeId) => themeById.get(themeId))
      .filter((theme): theme is Theme => Boolean(theme));
  }, [shortlistedThemeIds, themeLibrary]);

  const handleExport = async (theme: Theme, event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    exportTheme(theme, pageType);
    if (user) {
      const content = generateDesignMd(theme, pageType);
      try {
        await saveExportHistory(user.id, theme, pageType, 'design_md', content);
        const records = await fetchExportHistory(user.id);
        setExportHistory(records);
      } catch {
        setLibraryStatus('error');
      }
    }
  };

  const copyPrompt = async (theme: Theme, event?: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    const prompt = generateToolPrompt(theme, pageType, locale);
    const didCopy = await copyTextToClipboard(prompt);
    if (didCopy && user) {
      try {
        await saveExportHistory(user.id, theme, pageType, 'prompt', prompt);
        const records = await fetchExportHistory(user.id);
        setExportHistory(records);
      } catch {
        setLibraryStatus('error');
      }
    }
    setCopyStatus(didCopy ? 'copied' : 'failed');
  };

  const toggleShortlist = async (theme: Theme, event?: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    const wasShortlisted = shortlistedThemeIds.includes(theme.id);
    setShortlistedThemeIds((currentThemeIds) => (
      currentThemeIds.includes(theme.id)
        ? currentThemeIds.filter((themeId) => themeId !== theme.id)
        : [...currentThemeIds, theme.id]
    ));

    if (user) {
      try {
        if (wasShortlisted) {
          await removeShortlistRecord(user.id, theme.id);
        } else {
          await syncShortlistRecord(user.id, theme.id, pageType, viewport);
        }
      } catch {
        setLibraryStatus('error');
      }
    }
  };

  const clearShortlist = async () => {
    setShortlistedThemeIds([]);
    if (user) {
      try {
        await clearShortlistRecords(user.id);
      } catch {
        setLibraryStatus('error');
      }
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
  };

  const toggleLocale = () => {
    setLocale((currentLocale) => (currentLocale === 'zh' ? 'en' : 'zh'));
  };

  const renderThemeActions = (theme: Theme, variant: 'card' | 'shortlist' = 'card') => {
    const actionText = getAccessibleTextColor(theme.colors.onPrimary, theme.colors.primary);
    const exportText = getAccessibleAccentColor(theme.colors.textSecondary, theme.colors.background, theme.colors.primary);
    const isShortlisted = shortlistedThemeIds.includes(theme.id);
    const compact = variant === 'shortlist';

    return (
      <div className={compact ? 'grid grid-cols-2 gap-2' : 'space-y-2'}>
        <button
          type="button"
          onClick={() => setSelectedTheme(theme)}
          className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{
            backgroundColor: theme.colors.primary,
            color: actionText,
            ['--tw-ring-color' as string]: theme.colors.primary,
          }}
        >
          <Eye size={16} aria-hidden="true" />
          {copy.previewFullPage}
        </button>

        <button
          type="button"
          onClick={(event) => handleExport(theme, event)}
          className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{
            backgroundColor: 'transparent',
            color: exportText,
            borderColor: theme.colors.border,
            ['--tw-ring-color' as string]: theme.colors.primary,
          }}
        >
          <Download size={16} aria-hidden="true" />
          {copy.exportDesignMd}
        </button>

        {!compact && (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={(event) => toggleShortlist(theme, event)}
              aria-pressed={isShortlisted}
              className="flex min-h-11 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{
                backgroundColor: isShortlisted ? theme.colors.surface : 'transparent',
                color: exportText,
                borderColor: isShortlisted ? theme.colors.primary : theme.colors.border,
                ['--tw-ring-color' as string]: theme.colors.primary,
              }}
            >
              {isShortlisted ? <Check size={16} aria-hidden="true" /> : <Heart size={16} aria-hidden="true" />}
              {isShortlisted ? copy.shortlisted : copy.addToShortlist}
            </button>

            <button
              type="button"
              onClick={(event) => copyPrompt(theme, event)}
              className="flex min-h-11 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{
                backgroundColor: 'transparent',
                color: exportText,
                borderColor: theme.colors.border,
                ['--tw-ring-color' as string]: theme.colors.primary,
              }}
            >
              <Copy size={16} aria-hidden="true" />
              {copy.exportPrompt}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (selectedTheme) {
    return (
      <PreviewFrame
        theme={selectedTheme}
        themes={themeLibrary}
        onClose={() => setSelectedTheme(null)}
        pageType={pageType}
        viewport={viewport}
        locale={locale}
        onViewportChange={setViewport}
        onPageTypeChange={setPageType}
        onThemeChange={(theme) => setSelectedTheme(theme)}
        isShortlisted={shortlistedThemeIds.includes(selectedTheme.id)}
        onToggleShortlist={(theme) => toggleShortlist(theme)}
      />
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: appShellColors.background }}>
      <header
        className="sticky top-0 z-40 border-b"
        style={{ backgroundColor: appShellColors.surface, borderColor: appShellColors.border }}
      >
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
          <div className="min-w-0">
            <BrandLogo className="flex items-center gap-3" />
            <p className="mt-2 max-w-3xl text-sm" style={{ color: appShellColors.textSecondary }}>
              {copy.subtitle}
            </p>
          </div>
          <div className="-mx-4 flex min-w-0 overflow-x-auto px-4 sm:mx-0 sm:px-0 lg:justify-self-end">
            <div className="flex min-w-max items-center gap-2">
              <button
                type="button"
                onClick={() => setShortlistOpen(true)}
                className="inline-flex min-h-11 w-[116px] flex-shrink-0 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                  backgroundColor: appShellColors.surface,
                  borderColor: appShellColors.border,
                  color: appShellColors.textPrimary,
                  ['--tw-ring-color' as string]: appShellColors.focusRing,
                }}
              >
                <Heart size={16} aria-hidden="true" />
                <span className="truncate">{copy.shortlistToggle(shortlistedThemes.length)}</span>
              </button>
              <button
                type="button"
                onClick={() => setValueWorkbenchOpen(true)}
                className="inline-flex min-h-11 w-[144px] flex-shrink-0 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                  backgroundColor: appShellColors.primary,
                  borderColor: appShellColors.primary,
                  color: appShellColors.onPrimary,
                  ['--tw-ring-color' as string]: appShellColors.focusRing,
                }}
              >
                <Sparkles size={16} aria-hidden="true" />
                <span className="truncate">{copy.valueWorkbench.entry}</span>
              </button>
              <AuthButton locale={locale} className="w-[148px] flex-shrink-0 sm:w-[168px]" />
              <button
                type="button"
                onClick={toggleLocale}
                aria-label={copy.languageLabel}
                className="inline-flex min-h-11 w-[112px] flex-shrink-0 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                  backgroundColor: appShellColors.surface,
                  borderColor: appShellColors.border,
                  color: appShellColors.textPrimary,
                  ['--tw-ring-color' as string]: appShellColors.focusRing,
                }}
              >
                <Languages size={16} aria-hidden="true" />
                <span className="truncate">{copy.languageToggle}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold" style={{ color: appShellColors.textPrimary }}>
              {copy.libraryTitle}
            </h1>
            <p className="text-sm" style={{ color: appShellColors.textSecondary }}>
              {copy.libraryDescription}
            </p>
          </div>
          <div className="-mx-4 flex min-w-0 overflow-x-auto px-4 sm:mx-0 sm:px-0 lg:justify-end">
            <div className="flex min-w-max items-center gap-3">
              <PageTypeToggle value={pageType} locale={locale} onChange={setPageType} />
              <ViewportSwitcher current={viewport} onChange={setViewport} locale={locale} theme={shellViewportTheme} />
            </div>
          </div>
        </div>

        <div className="mb-6 space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative max-w-xl flex-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: appShellColors.textMuted }}
                aria-hidden="true"
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                aria-label={copy.searchLabel}
                placeholder={copy.searchPlaceholder}
                className="min-h-11 w-full rounded-lg border py-2.5 pl-9 pr-3 text-sm outline-none transition focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                  backgroundColor: appShellColors.surface,
                  borderColor: appShellColors.border,
                  color: appShellColors.textPrimary,
                  ['--tw-ring-color' as string]: appShellColors.focusRing,
                }}
              />
            </div>
            <p className="text-sm" style={{ color: appShellColors.textMuted }}>
              {copy.showing(filteredThemes.length, themeLibrary.length)}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedCategory('All')}
              aria-pressed={selectedCategory === 'All'}
              className="min-h-11 rounded-lg border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{
                backgroundColor: selectedCategory === 'All' ? appShellColors.primary : appShellColors.surface,
                borderColor: selectedCategory === 'All' ? appShellColors.primary : appShellColors.border,
                color: selectedCategory === 'All' ? appShellColors.onPrimary : appShellColors.textSecondary,
                ['--tw-ring-color' as string]: appShellColors.focusRing,
              }}
            >
              {copy.allCategory} ({themeLibrary.length})
            </button>
            {THEME_CATEGORIES.map((category) => {
              const isActive = selectedCategory === category;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setSelectedCategory(category)}
                  aria-pressed={isActive}
                  className="min-h-11 rounded-lg border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{
                    backgroundColor: isActive ? appShellColors.primary : appShellColors.surface,
                    borderColor: isActive ? appShellColors.primary : appShellColors.border,
                    color: isActive ? appShellColors.onPrimary : appShellColors.textSecondary,
                    ['--tw-ring-color' as string]: appShellColors.focusRing,
                  }}
                >
                  {getCategoryLabel(category, locale)} ({categoryCounts[category]})
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredThemes.map((theme) => {
            const category = getThemeCategory(theme);
            const cardTextPrimary = getAccessibleTextColor(theme.colors.textPrimary, theme.colors.background);
            const cardTextSecondary = getAccessibleTextColor(theme.colors.textSecondary, theme.colors.background);
            const cardTextMuted = getAccessibleTextColor(theme.colors.textMuted, theme.colors.background);
            const surfaceTextMuted = getAccessibleTextColor(theme.colors.textMuted, theme.colors.surface);
            const isShortlisted = shortlistedThemeIds.includes(theme.id);

            return (
              <article
                key={theme.id}
                className="overflow-hidden rounded-lg border transition-shadow hover:shadow-lg"
                style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border }}
              >
                <ThemeMiniPreview theme={theme} pageType={pageType} viewport={viewport} />

                <div className="p-4">
                  <div className="mb-3 flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="truncate font-semibold" style={{ color: cardTextPrimary }}>
                        {theme.name}
                      </h2>
                      <p className="mt-0.5 text-xs" style={{ color: cardTextMuted }}>
                        {getCategoryLabel(category, locale)} / {getDensityLabel(theme.mood.density, locale)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(event) => toggleShortlist(theme, event)}
                      aria-label={isShortlisted ? copy.removeFromShortlist : copy.addToShortlist}
                      aria-pressed={isShortlisted}
                      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      style={{
                        backgroundColor: isShortlisted ? theme.colors.surface : 'transparent',
                        borderColor: isShortlisted ? theme.colors.primary : theme.colors.border,
                        color: getAccessibleAccentColor(theme.colors.textSecondary, theme.colors.background, theme.colors.primary),
                        ['--tw-ring-color' as string]: theme.colors.primary,
                      }}
                      title={isShortlisted ? copy.removeFromShortlist : copy.addToShortlist}
                    >
                      {isShortlisted ? <Check size={16} aria-hidden="true" /> : <Heart size={16} aria-hidden="true" />}
                    </button>
                  </div>

                  <p className="mb-4 line-clamp-3 text-xs" style={{ color: cardTextSecondary }}>
                    {getThemeCardSummary(theme, locale)}
                  </p>

                  {locale === 'en' && (
                    <div className="mb-4 flex flex-wrap gap-1.5">
                      {theme.visualKeywords.slice(0, 3).map((keyword) => (
                        <span
                          key={keyword}
                          className="rounded-full px-2 py-1 text-xs"
                          style={{ backgroundColor: theme.colors.surface, color: surfaceTextMuted }}
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2 p-4 pt-0">
                  {renderThemeActions(theme)}
                </div>
              </article>
            );
          })}
        </div>

        {filteredThemes.length === 0 && (
          <div
            className="rounded-lg border px-6 py-12 text-center"
            style={{ backgroundColor: appShellColors.surface, borderColor: appShellColors.border }}
          >
            <h2 className="text-base font-semibold" style={{ color: appShellColors.textPrimary }}>
              {copy.noMatchesTitle}
            </h2>
            <p className="mt-2 text-sm" style={{ color: appShellColors.textMuted }}>
              {copy.noMatchesBody}
            </p>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-5 min-h-11 rounded-lg px-4 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{
                backgroundColor: appShellColors.primary,
                color: appShellColors.onPrimary,
                ['--tw-ring-color' as string]: appShellColors.focusRing,
              }}
            >
              {copy.resetFilters}
            </button>
          </div>
        )}
      </main>

      <footer
        className="border-t py-6"
        style={{ backgroundColor: appShellColors.surface, borderColor: appShellColors.border }}
      >
        <div
          className="mx-auto flex max-w-7xl flex-col gap-2 px-4 text-sm sm:px-6 md:flex-row md:items-center md:justify-between"
          style={{ color: appShellColors.textMuted }}
        >
          <p>{copy.footer.note}</p>
          <a
            href="https://github.com/nexu-io/open-design/tree/main/design-systems"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{ color: appShellColors.focusRing, ['--tw-ring-color' as string]: appShellColors.focusRing }}
          >
            {copy.footer.sourcePrefix}: open-design
          </a>
        </div>
      </footer>

      {shortlistOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/30">
          <aside
            className="flex h-full w-full max-w-md flex-col border-l shadow-2xl"
            style={{ backgroundColor: appShellColors.surface, borderColor: appShellColors.border }}
            aria-label={copy.shortlistTitle}
          >
            <div className="border-b p-4" style={{ borderColor: appShellColors.border }}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold" style={{ color: appShellColors.textPrimary }}>
                    {copy.shortlistTitle}
                  </h2>
                  <p className="mt-1 text-sm" style={{ color: appShellColors.textSecondary }}>
                    {copy.shortlistDescription}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShortlistOpen(false)}
                  aria-label={copy.shortlistClose}
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{ color: appShellColors.textPrimary, ['--tw-ring-color' as string]: appShellColors.focusRing }}
                >
                  <X size={20} aria-hidden="true" />
                </button>
              </div>

              {shortlistedThemes.length > 0 && (
                <button
                  type="button"
                  onClick={clearShortlist}
                  className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{
                    backgroundColor: appShellColors.surface,
                    borderColor: appShellColors.border,
                    color: appShellColors.textSecondary,
                    ['--tw-ring-color' as string]: appShellColors.focusRing,
                  }}
                >
                  <Trash2 size={16} aria-hidden="true" />
                  {copy.shortlistClear}
                </button>
              )}
            </div>

            <div className="flex-1 overflow-auto p-4">
              {shortlistedThemes.length === 0 ? (
                <div
                  className="rounded-lg border p-6 text-center"
                  style={{ backgroundColor: appShellColors.background, borderColor: appShellColors.border }}
                >
                  <h3 className="font-semibold" style={{ color: appShellColors.textPrimary }}>
                    {copy.shortlistEmptyTitle}
                  </h3>
                  <p className="mt-2 text-sm" style={{ color: appShellColors.textMuted }}>
                    {copy.shortlistEmptyBody}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {shortlistedThemes.map((theme) => {
                    const cardTextPrimary = getAccessibleTextColor(theme.colors.textPrimary, theme.colors.background);
                    const cardTextMuted = getAccessibleTextColor(theme.colors.textMuted, theme.colors.background);

                    return (
                      <article
                        key={theme.id}
                        className="overflow-hidden rounded-lg border"
                        style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border }}
                      >
                        <ThemeMiniPreview theme={theme} pageType={pageType} viewport={viewport} />
                        <div className="space-y-3 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="truncate font-semibold" style={{ color: cardTextPrimary }}>
                                {theme.name}
                              </h3>
                              <p className="text-xs" style={{ color: cardTextMuted }}>
                                {getCategoryLabel(getThemeCategory(theme), locale)} / {getDensityLabel(theme.mood.density, locale)}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={(event) => toggleShortlist(theme, event)}
                              aria-label={copy.removeFromShortlist}
                              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                              style={{
                                backgroundColor: 'transparent',
                                borderColor: theme.colors.border,
                                color: getAccessibleAccentColor(theme.colors.textSecondary, theme.colors.background, theme.colors.primary),
                                ['--tw-ring-color' as string]: theme.colors.primary,
                              }}
                              title={copy.removeFromShortlist}
                            >
                              <Trash2 size={16} aria-hidden="true" />
                            </button>
                          </div>
                          {renderThemeActions(theme, 'shortlist')}
                          <button
                            type="button"
                            onClick={(event) => copyPrompt(theme, event)}
                            className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                            style={{
                              backgroundColor: 'transparent',
                              color: getAccessibleAccentColor(theme.colors.textSecondary, theme.colors.background, theme.colors.primary),
                              borderColor: theme.colors.border,
                              ['--tw-ring-color' as string]: theme.colors.primary,
                            }}
                          >
                            <Copy size={16} aria-hidden="true" />
                            {copy.exportPrompt}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>
        </div>
      )}

      {copyStatus !== 'idle' && (
        <div
          className="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 rounded-lg px-4 py-3 text-sm font-medium shadow-lg"
          style={{
            backgroundColor: copyStatus === 'copied' ? appShellColors.primary : '#991b1b',
            color: appShellColors.onPrimary,
          }}
          role="status"
        >
          {copyStatus === 'copied' ? copy.copiedPrompt : copy.promptCopyFailed}
        </div>
      )}

      {libraryStatus === 'syncing' && (
        <div
          className="fixed bottom-4 right-4 z-[60] rounded-lg border px-3 py-2 text-sm shadow-lg"
          style={{ backgroundColor: appShellColors.surface, borderColor: appShellColors.border, color: appShellColors.textSecondary }}
          role="status"
        >
          {copy.valueWorkbench.syncing}
        </div>
      )}

      {libraryStatus === 'error' && (
        <div
          className="fixed bottom-4 right-4 z-[60] rounded-lg px-3 py-2 text-sm font-medium shadow-lg"
          style={{ backgroundColor: '#991b1b', color: appShellColors.onPrimary }}
          role="status"
        >
          {copy.valueWorkbench.syncFailed}
        </div>
      )}

      {valueWorkbenchOpen && (
        <ValueWorkbench
          customThemes={customThemes}
          exportHistory={exportHistory}
          locale={locale}
          onClose={() => setValueWorkbenchOpen(false)}
          onRefreshLibrary={refreshUserLibrary}
          pageType={pageType}
          user={user}
          viewport={viewport}
        />
      )}
    </div>
  );
}
